import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import Hero from "../../components/About/hero/Hero";
import { PRICING_PLANS, ADD_ON_SERVICES, formatPrice } from "../../data/pricing";
import { useCart } from "../../context/CartContext";
import { IMAGES } from "../../data/images";
import { Animate, Stagger } from "../../components/animations";

function Pricing() {
  const navigate = useNavigate();
  const { addToCart } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handlePurchase = (plan) => {
    addToCart({
      id: plan.id,
      name: plan.name,
      price: plan.price,
      currency: plan.currency,
      type: "plan",
    });
    navigate("/checkout");
  };

  const handleAddOnPurchase = (addon) => {
    addToCart({
      id: addon.id,
      name: addon.name,
      price: addon.price,
      currency: addon.currency,
      type: "addon",
    });
    navigate("/checkout");
  };

  return (
    <>
      <Header />
      <Hero heading="Pricing & Packages" />
      <section
        className="layout-band layout-section"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.94), rgba(255,255,255,0.94)), url(${IMAGES.pricingBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="layout-container">
          <Animate variant="fadeUp" className="text-center mb-12">
            <h4 className="section-label">Transparent Pricing</h4>
            <h1 id="heading-large" className="mt-3">
              Choose the Right Plan for Your Journey
            </h1>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto leading-relaxed">
              No hidden fees. Pay once and get full access to your chosen services.
              All plans include a free initial consultation.
            </p>
          </Animate>

          <Stagger
            className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-20"
            variant="fadeUp"
            stagger={140}
          >
            {PRICING_PLANS.map((plan) => (
              <div
                key={plan.id}
                className={`relative rounded-2xl p-6 sm:p-8 flex flex-col bg-white transition-all duration-300 anim-hover-lift ${
                  plan.popular
                    ? "border-2 border-brand-light shadow-card-hover lg:scale-[1.02] ring-4 ring-brand-light/10"
                    : "border border-slate-200 shadow-card hover:shadow-card-hover"
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-brand-light to-brand-teal text-white px-5 py-1 rounded-full text-xs font-bold tracking-wide uppercase">
                    Most Popular
                  </span>
                )}
                <h2 id="heading-md" className="text-center">
                  {plan.name}
                </h2>
                <div className="text-center my-6">
                  <span className="text-4xl font-extrabold text-brand-light">
                    {formatPrice(plan.price)}
                  </span>
                  <span className="text-slate-400 ml-1 text-sm">/ {plan.period}</span>
                </div>
                <p className="text-slate-600 text-center mb-6 text-sm">{plan.description}</p>
                <ul className="space-y-3 mb-8 flex-grow">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <span className="text-brand-teal font-bold mt-0.5">✓</span>
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handlePurchase(plan)}
                  className={plan.popular ? "btn-primary w-full" : "btn-outline w-full"}
                >
                  Buy Now
                </button>
              </div>
            ))}
          </Stagger>

          <Animate variant="fadeUp" className="text-center mb-12">
            <h1 id="heading-large">Individual Services</h1>
            <p className="mt-4 text-slate-600">
              Need just one service? Purchase individually.
            </p>
          </Animate>

          <Stagger
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
            variant="scaleIn"
            stagger={90}
          >
            {ADD_ON_SERVICES.map((addon) => (
              <div key={addon.id} className="card p-6 flex flex-col anim-hover-lift">
                <h3 id="heading-md" className="!text-base">
                  {addon.name}
                </h3>
                <p className="text-2xl font-extrabold text-brand-light my-3">
                  {formatPrice(addon.price)}
                </p>
                <p className="text-slate-600 mb-5 text-sm flex-grow">{addon.description}</p>
                <button
                  onClick={() => handleAddOnPurchase(addon)}
                  className="btn-primary w-full !py-2.5"
                >
                  Purchase
                </button>
              </div>
            ))}
          </Stagger>
        </div>
      </section>
      <Footer />
    </>
  );
}

export default Pricing;
